import { Component, OnInit, OnDestroy, HostListener, ViewChild, Input, SimpleChanges, OnChanges } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import * as dayjs from 'dayjs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';
import { Event } from '../../model/event';
import { EventsService } from '../../service/events.service';
import { EventInfo } from '../../model/eventInfo';
import { NavbarService } from 'src/app/service/navbar.service';
import { InfoService } from 'src/app/service/info.service';
import { AuthService } from 'src/app/service/auth.service';
import { UserEventAnswer } from '../../enum/user.event.answer';
import { LanguageService } from '../../../app/service/language.service';
import { TimezoneService } from 'src/app/service/timezone.service';
import { User } from 'src/app/model/user';
import { UserEventRole } from '../../enum/user.event.role';
import { AdminService } from 'src/app/service/admin.service';
import { EventUserViewComponent } from '../event-user-view/event-user-view.component';
import { ReviewsConfigurationService } from 'src/app/service/reviews.configuration.service';
import { EventReviewConfiguration } from 'src/app/model/eventReviewConfiguration';


@Component({
  selector: 'app-event-view',
  templateUrl: './event-view.component.html',
  styleUrls: ['./event-view.component.scss']
})
export class EventViewComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild(EventUserViewComponent) eventUserView!: EventUserViewComponent;

  /** Modo prévia: quando um `event` chega por Input, esta instância é usada
   *  como widget (embutido, ex.: em event-settings) e NÃO como página. Pula
   *  todas as subscrições de route e todos os side effects globais
   *  (`adminService.selectedEvent`, `infoService.selectedEvent`, título do
   *  browser, submissions, checkIfEventCommittee, checkIfEventChairOrAdmin).
   *  Só fetcha `chairs` — necessário pra popular a lista "Coordenador de TPC"
   *  do card. */
  @Input() eventInput?: Event;
  public event: Event;

  /** True quando a instância está em modo prévia (embutida via `eventInput`,
   *  não como página). Usado pelo template pra esconder tudo que vem DEPOIS
   *  do card do topo (committee-card, `<app-event-user-view>` etc.) —
   *  na prévia só interessa o card. */
  get previewMode(): boolean { return !!this.eventInput; }
  public eventChairs: Array<User>
  public info: EventInfo;

  private destroy$ = new Subject<void>();
  private _today: dayjs.Dayjs;

  public userIsCommiteeMember = false;
  public userHasPendingInvite = false;
  public showEventAdminButton = false;
  // Verdadeiro quando o usuário É publication chair e NÃO é chair/admin.
  // O chair já vê "Gerenciar evento" (que dá acesso amplo à admin); o
  // publication chair puro vê "Gerenciar publicações" na mesma posição
  // e cai numa versão restrita do mesmo sidebar admin.
  public showManagePublicationsButton = false;

  public userIsChair = false;
  public userIsPublicationChair = false;

  public timeZoneEvent: string;
  public timeZoneEventOffset: string;
  public loadingSubmissions: boolean = false;
  public reviewConfig: EventReviewConfiguration;

  constructor(
    public adminService: AdminService,
    public router: Router, public route: ActivatedRoute,
    public eventService: EventsService, public location: Location,
    public navbarService: NavbarService, public infoService: InfoService,
    public authService: AuthService,
    public languageService: LanguageService,
    public TimezoneService: TimezoneService,
    private titleService: Title,
    private reviewsConfigService: ReviewsConfigurationService
  ) { }

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    this.location.back();
  }

  ngOnInit() {
    // Modo prévia: `eventInput` chega via @Input → pula todas as subscrições
    // de route (não estamos numa página, estamos embutidos como widget).
    if (this.eventInput) {
      this.applyPreviewEvent(this.eventInput);
      return;
    }
    setTimeout(() => {
      // O Router REUSA o EventViewComponent quando se navega entre rotas
      // da mesma definição (`:slug` ou `events/:id`). Subscrever os
      // observables (e não ler `snapshot`) garante que `applyEvent` rode
      // a CADA mudança de rota.
      //
      // Rota `/:slug` → resolver injeta o evento em `data.event` e cuida
      //                 do dim durante o fetch (loadingStart/Stop dele).
      // Rota `/events/:id` → não tem resolver; ngOnInit faz o ciclo do
      //                       dim em torno do fetch local.
      this.route.data.pipe(takeUntil(this.destroy$)).subscribe(data => {
        if (data?.event) {
          this.applyEvent(data.event);
        }
      });
      this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
        if (params.id) {
          this.navbarService.loadingStart();
          this.eventService.getEvent(parseInt(params.id, 10)).pipe(takeUntil(this.destroy$)).subscribe(
            e => this.applyEvent(e),
            () => this.navbarService.loadingStop(),
          );
        }
      });
      if(this.event){
        this.reviewsConfigService.getReviewsConfiguration(this.event.id).subscribe(config => {
          this.reviewConfig = config;
        });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Prévia: eventInput muda depois do ngOnInit (ex.: getter reativo do
    // parent) → reaplica sem re-disparar side effects.
    if (changes.eventInput && this.eventInput) {
      this.applyPreviewEvent(this.eventInput);
    }
  }

  /** Versão "silenciosa" de applyEvent — só seta `event` local e busca
   *  chairs (pra popular "Coordenador de TPC"). NADA de tocar em serviços
   *  globais, title do browser, checagens de papel, submissions, etc.
   *  O card renderiza apenas com o evento passado. */
  private applyPreviewEvent(e: Event): void {
    this.event = e;
    // Sub-eventos: `event.subconferences` pode ou não vir populado no evento
    // passado; se vier, ordena (mesma lógica de applyEvent).
    if (this.event.subconferences) {
      this.sortEvents(this.event.subconferences);
    }
    // Chairs pra popular "Coordenador de TPC". Fetch idempotente (backend
    // cache-friendly); sem loadingStart do navbar (não é uma navegação).
    if (this.event.id) {
      this.getEventChairs();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Handler pro `app-tpc-invite-panel` embutido: após aceitar/rejeitar,
  // reload da página do evento pra que `userEvent.invitationAnswer`
  // reflita o novo estado (aceito → aparece "Ações de membro do comitê";
  // rejeitado → volta pra listagem).
  public onInviteAnswered(answer: string): void {
    if (String(answer).toUpperCase() === 'ACCEPTED') {
      this.navbarService.loadingStart();
      this.eventService.getEvent(this.event.id).pipe(takeUntil(this.destroy$)).subscribe(
        e => this.applyEvent(e),
        () => this.navbarService.loadingStop(),
      );
    } else {
      // Rejeição: sai da página do evento — o usuário não é mais convidado.
      this.location.back();
    }
  }

  private applyEvent(e: any): void {
    // Desliga o dim ASSIM QUE temos o evento — a nova página já pode
    // começar a renderizar. Sub-loads (chairs, comitês) acontecem em
    // background, sem manter o conteúdo esmaecido.
    this.navbarService.loadingStop();

    this.adminService.selectedEvent = e;
    this.event = e;
    this.infoService.selectedEvent = this.event;
    this.getEventChairs();
    this.updateHeadTitle();
    this.checkIfEventCommittee();
    this.checkIfEventChairOrAdmin();
    if (this.event.subconferences) {
      this.sortEvents(this.event.subconferences);
    }
    // After loading new event, make sure that the event-user-view app is loaded again
    this.reloadSubmissions();
  }

  public getEventChairs(): void {
    this.eventService.getEventMinimalPeopleByRole(this.event.id, UserEventRole.CHAIR).pipe(takeUntil(this.destroy$)).subscribe(chairs => {
      this.eventChairs = chairs.map(chair => chair.user);
    });
  }

  // Coordenadores distribuídos em até 3 colunas, preenchidas COLUNA-A-COLUNA:
  // a col1 enche até 3 nomes, então o 4º vai para a col2, etc. (com >9 nomes,
  // mantém 3 colunas e cada uma cresce). Usado pelo layout em colunas justas.
  get chairColumns(): Array<Array<User>> {
    const chairs = this.eventChairs || [];
    const perCol = Math.max(3, Math.ceil(chairs.length / 3));
    const cols: Array<Array<User>> = [];
    for (let i = 0; i < chairs.length; i += perCol) {
      cols.push(chairs.slice(i, i + perCol));
    }
    return cols;
  }

  reloadSubmissions(){
    this.loadingSubmissions = true;
    setTimeout(() => {
      this.loadingSubmissions = false;
    });

  }
  updateHeadTitle(){
    let eventAcronymYear = this.event.acronym + " " + this.event.year + " - ";
    let oldTitle = this.titleService.getTitle();
    if (!oldTitle.includes(eventAcronymYear)){
      this.titleService.setTitle(eventAcronymYear + this.titleService.getTitle());
    }
  }

  get baseUrl(): string {
    return window.location.origin;
  }

  checkIfEventCommittee() {
    const userEvent = this.event.userEvent;
    if (userEvent && userEvent.user !== null) {
      this.applyCommitteeFlags(userEvent);
    } else {
      // A página pública carrega o evento por slug (endpoint byURI), que NÃO
      // retorna `userEvent`. Busca essa relação do endpoint by-id (sem cache)
      // para saber se o usuário é membro de comitê / tem convite pendente.
      this.eventService.getUserEventForEvent(this.event.id).pipe(takeUntil(this.destroy$)).subscribe(ue => {
        if (ue && ue.user !== null) {
          this.event.userEvent = ue;
          this.applyCommitteeFlags(ue);
        }
      });
    }
  }

  private applyCommitteeFlags(userEvent: any): void {
    this.userIsCommiteeMember = !!userEvent && (userEvent?.invitationAnswer?.toUpperCase() === UserEventAnswer.ACCEPTED.toUpperCase());
    this.userHasPendingInvite = !!userEvent && (userEvent?.invitationAnswer?.toUpperCase() === UserEventAnswer.NOT_ANSWERED.toUpperCase())
                                            && (!!userEvent?.invitationSentAt);
  }

  checkIfEventChairOrAdmin(): void {
    this.authService.userIsAdminOrChair(this.event.id).pipe(takeUntil(this.destroy$)).subscribe(isAdminOrChair => {
      this.showEventAdminButton = isAdminOrChair;
      this.userIsChair = isAdminOrChair;
      // Só consulta publication-chair se NÃO é chair/admin — chair puro
      // dispensa: já vai ver o botão "Gerenciar evento".
      if (!isAdminOrChair) {
        this.authService.directUserIsUserEvent(this.event.id, UserEventRole.PUBLICATION_CHAIR)
          .pipe(takeUntil(this.destroy$))
          .subscribe(isPubChair => {
            this.userIsPublicationChair = isPubChair;
            this.showManagePublicationsButton = isPubChair;
          });
      }
    });
  }

  today() {
    return this._today ?? (this._today = dayjs());
  }

  public isEventPublic(eventPublicAt): boolean {
    return this.today().isAfter(eventPublicAt);
  }

  public sortEvents(events: Event[]): void{
    events.sort((a,b) => this.formatName(a) < this.formatName(b) ? -1 : this.formatName(a) > this.formatName(b) ? 1 : 0);
  }

  public formatName(event: Event): string{
    return `${event.year ? event.acronym + " " +  event.year.toString() : event.acronym} - ${event.name}`
  }
}
